import { HotelRoomCalendarPropertiesType } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card/hotel-room-calendar/hotel-room-calendar.types";
import { DatepickerSelectedDateType } from "@website/ui/datepicker";
import { dateFormat } from "@website/utils/date-format";
import { deepOmit } from "@website/utils/deep-omit";
import { isFilled } from "@website/utils/is-filled";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
import keyBy from "lodash-es/keyBy";
import toString from "lodash-es/toString";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";

export const useRoomCalendar = (
  properties: HotelRoomCalendarPropertiesType
) => {
  const router = useRouter();

  const { calendar, suggestions, isLabelToTop } = properties;
  const { date_to, date_from } = router.query as Record<string, string>;

  const onCalendarChange = useCallback(
    (date: any) => {
      setSelectedDate(date);
      const date_from = date[0]?.dateTime;
      const date_to = date[1]?.dateTime;

      if (date_to) {
        return router.replace(
          {
            pathname: router.pathname,
            query: deepOmit(
              {
                ...router.query,
                date_from,
                date_to
              },
              isFilled
            )
          },
          undefined,
          { scroll: false, shallow: true }
        );
      }
    },
    [router]
  );

  const datePickerInitialValues: DatepickerSelectedDateType = useMemo(
    () => [
      {
        year: Number(
          dateFormat(date_from, {
            locale: router.locale,
            numberConvert: false,
            template: "yyyy"
          })
        ),
        month: Number(
          dateFormat(date_from, {
            locale: router.locale,
            numberConvert: false,
            template: "MM"
          })
        ),
        day: Number(
          dateFormat(date_from, {
            locale: router.locale,
            numberConvert: false,
            template: "dd"
          })
        ),
        dateTime: toString(
          dateFormat(date_from, {
            locale: "en",
            template: "yyyy-MM-dd"
          })
        )
      },
      {
        year: Number(
          dateFormat(date_to, {
            locale: router.locale,
            numberConvert: false,
            template: "yyyy"
          })
        ),
        month: Number(
          dateFormat(date_to, {
            locale: router.locale,
            numberConvert: false,
            template: "MM"
          })
        ),
        day: Number(
          dateFormat(date_to, {
            locale: router.locale,
            numberConvert: false,
            template: "dd"
          })
        ),
        dateTime: toString(
          dateFormat(date_to, {
            locale: "en",
            template: "yyyy-MM-dd"
          })
        )
      }
    ],
    [date_from, date_to, router]
  );

  const [selectedDate, setSelectedDate] = useState<DatepickerSelectedDateType>(
    datePickerInitialValues
  );

  const startDateIndex = useMemo(
    () => calendar?.findIndex(({ date }) => date === selectedDate[0]?.dateTime),
    [calendar, selectedDate]
  );

  const endDateIndex = useMemo(
    () => calendar?.findIndex(({ date }) => date === selectedDate[1]?.dateTime),
    [calendar, selectedDate]
  );

  const transferredCalenderData = useMemo(
    () =>
      keyBy(
        calendar?.map(
          ({ date, isAvailable, price = 0, priceOff = 0 }, index) => {
            const canSelect =
              index >= startDateIndex &&
              startDateIndex !== -1 &&
              [index, -1].includes(endDateIndex)
                ? calendar
                    .slice(startDateIndex, index)
                    .every(({ isAvailable }) => isAvailable)
                : isAvailable;
            const convertedPrice = price
              ? numberConvert(numberSeparator(price / 1000), {
                  locale: router.locale,
                  numberOnly: false
                })
              : 0;
            return {
              date,
              state: canSelect ? undefined : ("disabled" as const),
              content: isAvailable ? (
                price > priceOff ? (
                  <div className="flex flex-col items-center">
                    <span className="line-through opacity-75">
                      {convertedPrice}
                    </span>
                    <span>
                      {priceOff
                        ? numberConvert(numberSeparator(priceOff / 1000), {
                            locale: router.locale,
                            numberOnly: false
                          })
                        : 0}
                    </span>
                  </div>
                ) : (
                  convertedPrice
                )
              ) : (
                "تکمیل"
              )
            };
          }
        ),
        "date"
      ),
    [calendar, endDateIndex, router.locale, startDateIndex]
  );

  return {
    selectedDate,
    setSelectedDate,
    calendarData: transferredCalenderData,
    disableLimit: {
      disableBeforeDate: calendar?.[0].date,
      disableAfterDate: calendar?.[calendar.length - 1].date
    },
    onCalendarChange,
    suggestions,
    isLabelToTop
  };
};
